import { useCallback } from 'react';
import useStatus from 'hooks/useStatus';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { stripHtmlTag } from 'utils/stripHtmlTag';
import { trackingInfoTransReport } from 'services/api/conversation';
import DOMPurify from 'dompurify';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { toUpperCase } from 'utils/toLowerCase';
import { useTradeListContext } from './TradeContext';
import { useTradeContext } from './StateContext';
import { tolgee } from 'services/i18n/tolgee';
import { useTranslateReport } from 'hooks/useTranslateReport';

export type PlatformLogistic = Exclude<General.PlatformUpperCase, 'TOKOPEDIA'>;
export type LogisticsPackage<P extends PlatformLogistic = PlatformLogistic> = {
  SHOPEE: Util.PartialRequired<
    Partial<Conversation.Trade.Logistics & Conversation.Trade.ThirdLogistics>,
    'leChatStatus' | 'internalTradeId' | 'externalTradeId' | 'trackingNumber' | 'trackingInfo'
  >;
  // TikTok 与 Shopee 接口一致
  TIKTOK: Util.PartialRequired<
    Partial<Conversation.Trade.Logistics & Conversation.Trade.ThirdLogistics>,
    'leChatStatus' | 'internalTradeId' | 'externalTradeId' | 'trackingNumber' | 'trackingInfo'
  >;
  TIKTOK_US: Util.PartialRequired<
    Partial<Conversation.Trade.Logistics & Conversation.Trade.ThirdLogistics>,
    'leChatStatus' | 'internalTradeId' | 'externalTradeId' | 'trackingNumber' | 'trackingInfo'
  >;
  ALIEXPRESS: Util.PartialRequired<
    Partial<Conversation.Trade.Logistics & Conversation.Trade.ThirdLogistics>,
    'leChatStatus' | 'internalTradeId' | 'externalTradeId' | 'trackingNumber' | 'trackingInfo'
  >;
  LAZADA: Conversation.Trade.LazadaLogisticsPackage;
  MERCADO: Conversation.Trade.MercadoLogistics;
}[P];

export function patchTranslatedResult(translatedList: string[], originList: string[]) {
  if (translatedList.length >= originList.length) {
    return translatedList;
  }

  for (let i = 0; i < originList.length; i += 1) {
    // 无原文确有译文，说明原文为空字符串而丢失了一项译文
    if (!originList[i] && translatedList[i]) {
      translatedList.splice(i, 0, originList[i]);
    }

    if (translatedList.length === originList.length) {
      return translatedList;
    }
  }

  // 在结尾补空字符串
  translatedList.push(...new Array(originList.length - translatedList.length).fill(''));

  return translatedList;
}

export const i18nLanguageTranslationLanguageMap: Record<
  General.SystemLanguage,
  keyof Conversation.Trade.Translation
> = {
  'zh-CN': 'zh',
  'en-US': 'en',
  id: 'id',
  th: 'th',
  tl: 'tl',
  es: 'es',
  vi: 'vi',
  pt: 'pt',
};

export function getCommentTranslation(
  translationMap: Conversation.Trade.Translation,
  lang?: General.SystemLanguage,
) {
  const i18nLanguage = lang || (tolgee.getLanguage() as General.SystemLanguage);

  const { [i18nLanguage]: translationLanguage = 'en' } = {
    ...i18nLanguageTranslationLanguageMap,
    'zh-CN': 'cn',
  } as unknown as Record<General.SystemLanguage, keyof Conversation.Trade.Translation>;

  if (translationLanguage === 'cn') {
    return translationMap?.[translationLanguage] || translationMap?.['zh'];
  }
  return translationMap?.[translationLanguage];
}

export function getTranslation(
  translationMap: Conversation.Trade.Translation,
  lang?: General.SystemLanguage,
) {
  const i18nLanguage = lang || (tolgee.getLanguage() as General.SystemLanguage);

  const { [i18nLanguage]: translationLanguage = 'en' } = i18nLanguageTranslationLanguageMap;

  return translationMap?.[translationLanguage];
}

function useTradeTranslate(reportTranslation = true) {
  const { pending, error, done } = useStatus();

  const [trade] = useTradeContext();

  const { setList, conversation } = useTradeListContext();

  const sysLang = useSystemLanguageValue();

  const translateReport = useTranslateReport();

  const translateTradeLogistics = useCallback(
    (
      platform: Exclude<PlatformLogistic, 'LAZADA'>,
      logisticsPackageList: LogisticsPackage<'SHOPEE'>[],
      isThird = false,
      isReverse = false,
    ) => {
      if (!conversation) {
        return Promise.reject(new Error('Conversation not exist'));
      }

      done.pending();

      const { externalTradeId } = trade;

      const { [sysLang]: lang } = i18nLanguageTranslationLanguageMap;

      const originTextList = logisticsPackageList.reduce<string[]>((res, pack) => {
        const { trackingInfo } = pack;

        return trackingInfo.reduce((res, track) => {
          const { description } = track;
          let text = description;
          try {
            text = DOMPurify.sanitize(decodeURIComponent(description), {
              RETURN_DOM: true,
            }).innerText;
          } catch (error) {
            //
          }

          res.push(text);

          return res;
        }, res);
      }, []);

      originTextList.forEach((v, i) => {
        originTextList[i] = stripHtmlTag(v);
      });
      return overseaTranslate(originTextList, sysLang as General.Language, 'side')
        .then((translatedList) => {
          const resList = patchTranslatedResult(translatedList, originTextList);
          setList((preList) => {
            const tradeIndex = preList.findIndex(
              (item) => item.externalTradeId === externalTradeId,
            );

            if (tradeIndex < 0) {
              return preList;
            }

            const { [tradeIndex]: preTrade } = preList;

            const { logisticsList, thirdLogisticsList, reverseLogisticsList } = preTrade;

            const target = isReverse
              ? reverseLogisticsList
              : isThird
              ? thirdLogisticsList
              : logisticsList;

            if (!target) {
              return preList;
            }

            let flag = 0;

            const nextTrade = {
              ...preTrade,
              [isReverse
                ? 'reverseLogisticsList'
                : isThird
                ? 'thirdLogisticsList'
                : 'logisticsList']: target.map((logistics) => {
                const { trackingInfo, ...others } = logistics;

                const nextTrackingInfo = trackingInfo.map((info) => {
                  const { descriptionTranslation, ...others } = info;

                  const result = {
                    ...others,
                    descriptionTranslation: {
                      ...descriptionTranslation,
                      [lang]: resList[flag],
                    },
                  };

                  flag += 1;

                  return result;
                });

                return {
                  ...others,
                  trackingInfo: nextTrackingInfo,
                };
              }),
            };

            return [...preList.slice(0, tradeIndex), nextTrade, ...preList.slice(tradeIndex + 1)];
          });

          done();

          return resList;
        })
        .then((resList) => {
          let flag = 0;

          if (!reportTranslation) {
            return;
          }

          const report = logisticsPackageList.reduce<
            Conversation.Trade.ShopeeTrackingInfoTransReportData[]
          >((res, pack) => {
            const {
              packageNumber,
              trackingInfo: { length },
            } = pack;

            if (!packageNumber) {
              return res;
            }

            const nextFlag = flag + length;

            const trans = resList.slice(flag, nextFlag);

            flag = nextFlag;

            res.push({
              packageNumber,
              translation: {
                [lang]: {
                  trans,
                },
              },
            });

            return res;
          }, []);

          if (report.length !== logisticsPackageList.length) {
            return;
          }

          trackingInfoTransReport({
            platform,
            region: trade.region,
            tradeId: externalTradeId,
            report,
          }).catch(() => {});
        })
        .catch((err) => {
          done.error(err);

          return Promise.reject(err);
        });
    },
    [conversation, done, setList, sysLang, trade, reportTranslation],
  );

  const translateLazadaLogistics = useCallback(
    (logisticsPackageList: LogisticsPackage<'LAZADA'>[]) => {
      if (!conversation) {
        return Promise.reject(new Error('Conversation not exist'));
      }

      done.pending();

      const { externalTradeId } = trade;

      const { [sysLang]: lang } = i18nLanguageTranslationLanguageMap;

      const originTextList = logisticsPackageList.reduce<string[]>((res, pack) => {
        const { trackingInfo } = pack;

        return trackingInfo.reduce((res, track) => {
          const { description } = track;

          res.push(description);

          return res;
        }, res);
      }, []);

      originTextList.forEach((v, i) => {
        originTextList[i] = stripHtmlTag(v);
      });
      return overseaTranslate(originTextList, sysLang as General.Language, 'side')
        .then((translatedList) => {
          const resList = patchTranslatedResult(translatedList, originTextList);

          setList((preList) => {
            const tradeIndex = preList.findIndex(
              (item) => item.externalTradeId === externalTradeId,
            );

            if (tradeIndex < 0) {
              return preList;
            }

            const { [tradeIndex]: preTrade } = preList;

            const { lazadaLogistics } = preTrade;

            if (!lazadaLogistics) {
              return preList;
            }

            let flag = 0;

            const nextTrade = {
              ...preTrade,
              lazadaLogistics: {
                ...lazadaLogistics,
                packages: lazadaLogistics.packages.map((pack) => {
                  const { trackingInfo, ...others } = pack;

                  return {
                    ...others,
                    trackingInfo: trackingInfo.map((info) => {
                      const { descriptionTranslation, ...others } = info;

                      const result = {
                        ...others,
                        descriptionTranslation: {
                          ...descriptionTranslation,
                          [lang]: resList[flag],
                        },
                      };

                      flag += 1;

                      return result;
                    }),
                  };
                }),
              },
            };

            return [...preList.slice(0, tradeIndex), nextTrade, ...preList.slice(tradeIndex + 1)];
          });

          done();

          return resList;
        })
        .then((resList) => {
          let flag = 0;

          const report = logisticsPackageList.map((pack) => {
            const {
              trackingNumber,
              trackingInfo: { length },
            } = pack as LogisticsPackage<'LAZADA'>;

            const nextFlag = flag + length;

            // lazada 物流在接口中是升序返回的, 界面降序展示,翻译也是降序, 上报时要转成升序
            const trans = resList.slice(flag, nextFlag).reverse();

            flag = nextFlag;

            return {
              trackingNumber,
              translation: {
                [lang]: {
                  trans,
                },
              },
            };
          });

          trackingInfoTransReport({
            platform: toUpperCase(trade.platform),
            region: trade.region,
            tradeId: externalTradeId,
            report,
          }).catch(() => {});
        })
        .catch((err) => {
          done.error(err);

          return Promise.reject(err);
        });
    },
    [conversation, done, setList, sysLang, trade],
  );

  const translateComment = useCallback(
    (comment: Conversation.Trade.Comment, itemIndex: number, commentIndex: number) => {
      if (!conversation) {
        return Promise.reject(new Error('Conversation not exist'));
      }

      done.pending();

      const { commentId, buyerComment } = comment;

      const { [sysLang]: lang } = i18nLanguageTranslationLanguageMap;

      return overseaTranslate(buyerComment, sysLang as General.Language, 'side')
        .then((res) => {
          if (!res) {
            return Promise.reject(new Error('Translation result is empty'));
          }

          setList((preList) => {
            return preList.map((curTrade) => {
              const { externalTradeId, items = [] } = curTrade;

              if (externalTradeId !== trade.externalTradeId) {
                return curTrade;
              }

              return {
                ...curTrade,
                items: items.map((item, index) => {
                  if (index !== itemIndex) {
                    return item;
                  }

                  const { itemComments = [] } = item;

                  return {
                    ...item,
                    itemComments: itemComments.map((comment, index) => {
                      if (index !== commentIndex) {
                        return comment;
                      }

                      return {
                        ...comment,
                        transBuyerComment: {
                          ...comment.transBuyerComment,
                          [lang]: res,
                        },
                      };
                    }),
                  };
                }),
              };
            });
          });

          done();

          return res;
        })
        .then((res) => {
          translateReport(
            {
              flag: 1,
              id: commentId,
              transComment: { [lang]: res },
            },
            'Review',
          );
        })
        .catch((err) => {
          done.error(err);

          return Promise.reject(err);
        });
    },
    [conversation, done, setList, sysLang, trade, translateReport],
  );

  const translateRefund = useCallback(
    (item: Conversation.Trade.Product, itemIndex: number) => {
      if (!conversation) {
        return Promise.reject(new Error('Conversation not exist'));
      }

      done.pending();

      const { refundReason: reasons = [] } = item;

      const { [sysLang]: lang } = i18nLanguageTranslationLanguageMap;

      const originTextList = reasons.reduce<string[]>((res, reason) => {
        const { textReason } = reason;

        res.push(textReason);

        return res;
      }, []);

      return overseaTranslate(originTextList, sysLang as General.Language, 'side')
        .then((resList) => {
          if (resList.length < originTextList.length) {
            return Promise.reject(
              new Error('Translation result list length is less than origin text list length'),
            );
          }

          setList((preList) => {
            return preList.map((curTrade) => {
              if (curTrade.externalTradeId !== trade.externalTradeId) {
                return curTrade;
              }

              const { items = [] } = curTrade;

              return {
                ...curTrade,
                items: items.map((curItem, index) => {
                  if (index !== itemIndex) {
                    return curItem;
                  }

                  const { refundReason = [] } = curItem;

                  return {
                    ...curItem,
                    refundReason: refundReason.map((curReason, index) => {
                      return {
                        ...curReason,
                        textReasonTrans: {
                          ...curReason.textReasonTrans,
                          [lang]: resList[index],
                        },
                      };
                    }),
                  };
                }),
              };
            });
          });

          done();

          return resList;
        })
        .then((resList) => {
          const data = reasons.map((reason, index) => {
            const { returnSn } = reason;

            return {
              returnSn,
              translation: {
                [lang]: resList[index],
              },
            };
          });

          translateReport(data, 'ReturnReason');
        })
        .catch((err) => {
          done.error(err);

          return Promise.reject(err);
        });
    },
    [conversation, done, setList, sysLang, trade, translateReport],
  );

  return {
    pending,
    error,
    done,
    translateTradeLogistics,
    translateLazadaLogistics,
    translateComment,
    translateRefund,
  };
}

export default useTradeTranslate;
